import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Tooltip, Message } from '@bijie/react-bnb';
import QRCode from 'qrcode.react';

import copyText from '~/utils/copy';
import { getAllCoinNetwork, getNetworkAddress } from '~/actions/actionsSet/recharge';

import IconWithBG from '~/components/IconWithBG';
import Icon from '~/components/Icon';

type Props = {
  rechargeProcut: Object,
  onCancel: Function,
  translate: Function,
  isLoggedIn: Boolean,
};

export default ({ rechargeProcut, onCancel, translate, isLoggedIn }: Props) => {
  const [networkList, setNetworkList] = useState([]);
  const [address, setAddress] = useState('');
  const [tag, setTag] = useState('');
  const [currentNetwork, setCurrentNetwork] = useState({});
  const { name, fullName, image } = rechargeProcut;

  useEffect(() => {
    if (!isLoggedIn) return;
    init();
  }, [isLoggedIn, name]);

  const getAddress = async network => {
    let res = await getNetworkAddress({
      network,
      coin: name,
      isAgreement: true,
      sameAddress: false,
    });
    if (!res) return setAddress('');
    setAddress(res.address);
    setTag(res.tag);
  };

  const init = async () => {
    let res = await getAllCoinNetwork();
    if (!res) return;
    const networkList = (res.find(item => item.coin === name) || {}).networkList;
    if (!networkList) return;
    getAddress(networkList[0].network);
    setNetworkList(networkList);
    setCurrentNetwork(networkList[0]);
  };

  const changeNetwork = item => {
    setCurrentNetwork(item);
    getAddress(item.network);
  };

  const copy = value => {
    copyText(value);
    Message.success('success');
  };

  return (
    <Wrapper>
      <GloablModalStyled />
      <div className="box">
        <div className="head">{translate('deposit')}</div>
        <div className="content">
          <div className="codeBox">
            <IconWithBG width={26} src={image} />
            <span className="bold">{name}</span>
            <span className="dark">{fullName}</span>
          </div>
          <div className="titleText">{`${translate('selectNetwork')}:`}</div>
          <div className="coinTypeBox">
            {networkList.map((item, index) => (
              <CoinType
                className="coinType"
                key={index}
                selected={currentNetwork.network === item.network}
                onClick={() => changeNetwork(item)}
              >
                {item.name}
                <Icon type="Checkmark" color="#F0B90B" size={20} />
              </CoinType>
            ))}
          </div>
          {tag && (
            <div className="tipBox">
              <Icon type="tips" size={20} />
              <span>{currentNetwork.specialTips}</span>
            </div>
          )}
          {tag && (
            <React.Fragment>
              <div className="titleText">{`${name} ${translate('deposit')} ${
                currentNetwork.label
              }`}</div>
              <div className="address">{tag}</div>
              <div className="btnBox">
                <div className="copy" onClick={() => copy(tag)}>
                  <Icon type="fuzhi2" />
                  <span>{`${translate('exchange-deposit-copy')} ${currentNetwork.label}`}</span>
                </div>
                <Tooltip
                  placement="top"
                  trigger={['hover']}
                  overlay={<QRCode value={tag} size={120} />}
                >
                  <div className="qrcode">
                    <Icon type="fuzhi2" />
                    <span>{translate('qrcode')}</span>
                  </div>
                </Tooltip>
              </div>
            </React.Fragment>
          )}
          <div className="titleText">{`${translate('address').replace(/{Symbol}/, name)}`}</div>
          <div className="address">{address}</div>
          <div className="copy" onClick={() => copy(address)}>
            <Icon type="fuzhi2" />
            <span>{translate('copyAddress')}</span>
          </div>
          <Tooltip
            placement="top"
            trigger={['hover']}
            overlay={<QRCode value={address} size={120} />}
          >
            <div className="qrcode">
              <Icon type="fuzhi2" />
              <span>{translate('qrcode')}</span>
            </div>
          </Tooltip>
        </div>
        <div className="close" onClick={onCancel}>
          <Icon type="Close-x" />
        </div>
      </div>
    </Wrapper>
  );
};

const GloablModalStyled = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;
const CoinType = styled.div.attrs(({ className }) => ({
  className,
}))`
  border: ${({ selected }) => (selected ? '1px solid #f0b90b' : '1px solid #EAECEF')};
  > i {
    display: ${({ selected }) => (selected ? 'flex' : 'none')};
  }
`;

const Wrapper = styled.div`
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  overflow: hidden;
  > .box {
    width: 470px;
    background: white;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.02);
    border-radius: 4px;
    position: relative;
    > .head {
      line-height: 56px;
      text-align: center;
      font-weight: 500;
      font-size: 16px;
      border-bottom: 2px solid #f0b90b;
    }
    > .content {
      padding: 32px;
      overflow: hidden;
      > .codeBox {
        display: flex;
        align-items: center;
        > .bold {
          font-weight: 500;
          font-size: 20px;
          margin: 0 4px 0 8px;
        }
        > .dark {
          font-size: 14px;
          color: #848e9c;
        }
      }
      > .tipBox {
        background: #fff2f5;
        border-radius: 4px;
        color: #f23051;
        padding: 10px 20px 10px 20px;
        display: flex;
        align-items: center;
        > span {
          margin-left: 10px;
        }
      }
      > .titleText {
        font-weight: 500;
        font-size: 14px;
        color: #48515d;
        margin: 32px 0 8px;
      }
      > .coinTypeBox {
        display: flex;
        flex-wrap: wrap;
        > .coinType {
          width: 125px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 500;
          font-size: 14px;
          border-radius: 3px;
          position: relative;
          margin: 0 10px 15px 0;
          > i {
            position: absolute;
            right: -6px;
            top: -6px;
            width: 16px;
            height: 16px;
            background: white;
            align-items: center;
            justify-content: center;
          }
        }
      }
      > .address {
        color: #f0b90b;
        font-weight: 600;
        font-size: 14px;
        line-height: 20px;
        word-break: break-all;
        margin-bottom: 16px;
      }
      > .btnBox {
        overflow: hidden;
      }
      .copy,
      .qrcode {
        display: flex;
        float: left;
        padding: 0 20px;
        height: 40px;
        align-items: center;
        justify-content: center;
        font-weight: 500;
        font-size: 14px;
        border-radius: 3px;
        border: 1px solid #eaecef;
        cursor: pointer;
        > span {
          margin-left: 10px;
        }
      }
      .qrcode {
        margin-left: 10px;
      }
    }
    > .close {
      position: absolute;
      right: -25px;
      top: 0px;
      color: white;
    }
  }
  @media (max-width: 767px) {
    > .box {
      width: 100%;
      height: 100%;
      border-radius: 0px;
      > .head {
        padding-top: 35px;
        font-size: 24px;
      }
      > .content {
        padding: 24px;
        > .coinTypeBox {
          > .coinType {
            width: 100px;
            height: 34px;
          }
        }
      }
      > .close {
        top: 28px;
        right: 28px;
        color: #848e9c;
      }
    }
  }
`;
